import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Loader2, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { useBranch } from "@/context/branch";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function CashFlowContent() {
  const [period, setPeriod] = useState("month");
  const { activeBranchId } = useBranch();

  const { data, isLoading } = useQuery({
    queryKey: ["accounting-cash-flow", period, activeBranchId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (activeBranchId) params.set("branchId", String(activeBranchId));
      const res = await fetch(`${BASE}/api/accounting/cash-flow?${params}`, { credentials: "include" });
      return res.json();
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const net = data?.netCashFlow ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Period:</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Inflows</span>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-xl font-bold text-emerald-600">{fmt(data?.totalInflow ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Outflows</span>
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <div className="text-xl font-bold text-red-500">{fmt(data?.totalOutflow ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${net >= 0 ? "border-l-blue-500" : "border-l-destructive"}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Net Cash Flow</span>
              <Wallet className={`w-4 h-4 ${net >= 0 ? "text-blue-500" : "text-destructive"}`} />
            </div>
            <div className={`text-xl font-bold ${net >= 0 ? "text-blue-600" : "text-destructive"}`}>{fmt(net)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Breakdown table */}
        <Card>
          <CardHeader><CardTitle>Cash Flow Breakdown</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-0">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2">Cash Inflows</div>
            {data?.breakdown?.filter((b: any) => b.type === "inflow").map((b: any) => (
              <div key={b.label} className="flex justify-between py-2.5 border-b border-border/40">
                <span className="text-sm">{b.label}</span>
                <span className="font-semibold text-emerald-600">+{fmt(b.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2.5 font-bold border-b-2 border-border">
              <span>Total Inflows</span>
              <span className="text-emerald-600">{fmt(data?.totalInflow ?? 0)}</span>
            </div>

            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2 pt-4">Cash Outflows</div>
            {data?.breakdown?.filter((b: any) => b.type === "outflow").map((b: any) => (
              <div key={b.label} className="flex justify-between py-2.5 border-b border-border/40">
                <span className="text-sm">{b.label}</span>
                <span className="font-semibold text-red-500">−{fmt(b.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2.5 font-bold border-b-2 border-border">
              <span>Total Outflows</span>
              <span className="text-red-500">{fmt(data?.totalOutflow ?? 0)}</span>
            </div>

            <div className={`flex justify-between py-3 font-bold text-base ${net >= 0 ? "text-emerald-600" : "text-destructive"}`}>
              <span>Net Cash Flow</span>
              <span>{net >= 0 ? "+" : ""}{fmt(net)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Monthly chart */}
        <Card>
          <CardHeader><CardTitle>6-Month Trend</CardTitle></CardHeader>
          <CardContent>
            {data?.monthlyChart?.length ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthlyChart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => [fmt(v), ""]} contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }} />
                    <Legend />
                    <Bar dataKey="inflow" name="Inflow" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="outflow" name="Outflow" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} fillOpacity={0.7} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function AccountingCashFlow() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Cash Flow — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">Track cash moving in and out of the selected location.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Cash Flow Statement">
        <CashFlowContent />
      </PlanGate>
    </div>
  );
}
